import { formatFromISODate } from '@odmonorepo/odfunctions';
import type { Application } from 'merchant-api/types';

/**
 * PROVE_STEP field visibility and prefill (XF-10207), keyed off data already on the application —
 * never off the entry surface or channelCode — so any future entry surface gets correct visibility
 * for free. Precedent: OwnerInfo's showName.
 *
 * What counts as "present" has two traps inherited from create:
 *  - XF-10189 persists a missing lastName / legalName as the literal string 'undefined' (Salesforce
 *    needs a non-empty value), so the sentinel is "missing".
 *  - Direct-apply BasicInfo collects no business name, so create seeds party.businesses[0].legalName
 *    with the owner's full name (getBizName). A legalName equal to the owner's name is "missing".
 * unified-api's isPlaceholderBusinessName applies the same rule when prove/complete decides whether
 * to overwrite the stored legalName; keep the two in step.
 */

type AppLike = Pick<Application, 'data'> | null | undefined;
type PersonLike = { firstName?: unknown; lastName?: unknown; dateOfBirth?: unknown; contacts?: unknown } | undefined;
type ContactLike = { type?: unknown; value?: unknown };

const SENTINEL = 'undefined';

export const isBlankOrSentinel = (value: unknown): boolean =>
  typeof value !== 'string' || !value.trim() || value.trim().toLowerCase() === SENTINEL;

const firstPerson = (app: AppLike): PersonLike => app?.data?.party?.persons?.[0] as PersonLike;

const contactValue = (person: PersonLike, types: string[]): string | undefined => {
  const contacts = (Array.isArray(person?.contacts) ? person?.contacts : []) as ContactLike[];
  const match = contacts.find((contact) => typeof contact?.type === 'string' && types.includes(contact.type) && !isBlankOrSentinel(contact.value));
  const value = match?.value;

  return typeof value === 'string' ? value.trim() : undefined;
};

export const getOwnerFullName = (person: PersonLike): string =>
  [person?.firstName, person?.lastName]
    .filter((value): value is string => !isBlankOrSentinel(value))
    .map((value) => value.trim())
    .join(' ');

/** data.email first (create writes it), else the applicant's EMAIL_ADDRESS contact. */
export const getApplicantEmail = (app: AppLike): string | undefined => {
  const direct = app?.data?.email;
  if (!isBlankOrSentinel(direct)) return (direct as string).trim();

  return contactValue(firstPerson(app), ['EMAIL_ADDRESS']);
};

/** create maps homePhone -> HOME_PHONE and bestContactNumber -> PHONE_NUMBER; accept either. */
export const getApplicantPhone = (app: AppLike): string | undefined =>
  contactValue(firstPerson(app), ['HOME_PHONE', 'PHONE_NUMBER']);

export const getApplicantDateOfBirth = (app: AppLike): string | undefined => {
  const dateOfBirth = firstPerson(app)?.dateOfBirth;

  return isBlankOrSentinel(dateOfBirth) ? undefined : (dateOfBirth as string).trim();
};

/** A business name the applicant actually gave us, or undefined when only a placeholder is stored. */
export const getApplicationBusinessName = (app: AppLike): string | undefined => {
  const direct = app?.data?.businessName;
  if (!isBlankOrSentinel(direct)) return (direct as string).trim();

  const legalName = app?.data?.party?.businesses?.[0]?.legalName;
  if (isBlankOrSentinel(legalName)) return undefined;

  const ownerFullName = getOwnerFullName(firstPerson(app));
  if (ownerFullName && (legalName as string).trim().toLowerCase() === ownerFullName.toLowerCase()) return undefined;

  return (legalName as string).trim();
};

export type ProveStepFieldVisibility = {
  businessName: boolean;
  email: boolean;
  dateOfBirth: boolean;
  phone: boolean;
};

export type ProveStepDefaults = {
  businessName: string;
  email: string;
  dateOfBirth: string;
  phoneNumber: string;
};

/**
 * Business name and email are hidden when already on the application. Date of birth is always
 * collected: no entry surface (MLP reduced create, direct-apply BasicInfo) asks for it, and Prove
 * needs it for the challenge. Phone is always shown and editable, prefilled when known.
 */
export const getProveStepFieldVisibility = (app: AppLike): ProveStepFieldVisibility => ({
  businessName: !getApplicationBusinessName(app),
  email: !getApplicantEmail(app),
  dateOfBirth: true,
  phone: true,
});

export const getProveStepDefaults = (app: AppLike): ProveStepDefaults => {
  const dateOfBirth = getApplicantDateOfBirth(app);

  return {
    businessName: getApplicationBusinessName(app) ?? '',
    email: getApplicantEmail(app) ?? '',
    dateOfBirth: dateOfBirth ? formatFromISODate(dateOfBirth) || '' : '',
    phoneNumber: getApplicantPhone(app) ?? '',
  };
};
